<template>
  <div class="file-preview-container" v-if="pdfData && pdfData.length > 0">
    <!-- حالت نمایش فایل تکی -->
    <div v-if="totalFilesCount === 1" class="single-file-view mb-4">
      <h4
        class="mb-3 px-2 font-weight-bold"
        v-if="pdfData[0] && pdfData[0].name"
        :style="{ color: activePalette.textColor }"
        :title="pdfData[0].name"
      >
        {{ pdfData[0].name }}
      </h4>

      <v-card
        outlined
        class="d-flex flex-column flex-sm-row rounded-lg overflow-hidden elevation-2"
        :style="{
          background: activePalette.bodyColor,
          borderColor: activePalette.borderColor,
        }"
      >
        <div
          class="thumbnail-wrapper d-flex align-center justify-center pa-4"
          :style="{ background: 'rgba(255,255,255,0.05)', minWidth: '160px' }"
        >
          <v-img
            :src="singleFile.url"
            max-height="130"
            max-width="130"
            contain
          ></v-img>
        </div>

        <div
          class="pa-4 flex-grow-1 d-flex flex-column justify-center"
          style="min-width: 0"
        >
          <h3
            class="text-h6 font-weight-bold mb-1 text-wrap-custom"
            :style="{ color: activePalette.textColor }"
            :title="singleFile.title"
          >
            {{ singleFile.title || "فایل ضمیمه" }}
          </h3>

          <div class="mb-4">
            <p
              class="text-body-2 mb-1 text-wrap-custom text-justify"
              :class="{ 'line-clamp-2': !isExpanded(singleFile.link) }"
              :style="{ color: activePalette.textColor, opacity: 0.8 }"
              :title="singleFile.description"
            >
              {{
                singleFile.description ||
                "برای مشاهده یا دانلود فایل از گزینه‌های زیر استفاده کنید."
              }}
            </p>
            <div
              v-if="hasLongText(singleFile.description)"
              @click="toggleDesc(singleFile.link)"
              class="read-more-btn"
              :style="{ color: activePalette.textColor }"
            >
              {{ isExpanded(singleFile.link) ? "بستن متن" : "مشاهده بیشتر..." }}
            </div>
          </div>

          <div class="d-flex flex-wrap gap-1 flex-row">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-on="on"
                  v-bind="attrs"
                  :class="
                    activeFile && activeFile.link === singleFile.link
                      ? 'btn-get-code rounded-lg'
                      : 'btn-download rounded-lg'
                  "
                  depressed
                  @click="openInline(singleFile)"
                  class="mb-2"
                >
                  <v-icon small>{{
                    activeFile && activeFile.link === singleFile.link
                      ? "mdi-eye-off"
                      : "mdi-eye"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>
                {{
                  activeFile && activeFile.link === singleFile.link
                    ? "بستن نمایش"
                    : "مشاهده فایل"
                }}</span
              >
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  outlined
                  :href="singleFile.link"
                  download
                  icon
                  class="btn-get-code rounded-lg"
                >
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>دانلود مستقیم</span>
            </v-tooltip>
          </div>
        </div>
      </v-card>
    </div>

    <!-- حالت نمایش چند فایل -->
    <div v-else-if="totalFilesCount > 1">
      <!-- ۱. حالت آکاردئون -->
      <v-expansion-panels v-if="activeLayout === 'accordion'" flat hover>
        <v-expansion-panel
          v-for="(category, index) in pdfData"
          :key="index"
          class="mb-2 rounded-lg border-all"
          :style="{
            background: activePalette.bodyColor,
            borderColor: activePalette.borderColor,
          }"
        >
          <v-expansion-panel-header
            class="font-weight-bold text-wrap-custom"
            :style="{
              color: activePalette.textColor,
              background: activePalette.headColor,
            }"
          >
            {{ category.name || "دسته بندی فایل‌ها" }}
          </v-expansion-panel-header>
          <v-expansion-panel-content
            :style="{
              background: activePalette.bodyColor,
              padding: '0px !important;',
              margin: '0px !important;',
            }"
          >
            <v-list dense class="pa-0" style="background: transparent">
              <template v-for="(file, fileIndex) in category.images">
                <v-list-item
                  :key="file.link"
                  class="file-list-item rounded py-2 px-1"
                  :style="{
                    background: 'rgba(255,255,255,0.05)',
                  }"
                >
                  <div class="d-flex flex-row w-100 align-center">
                    <v-avatar
                      rounded
                      size="72"
                      class="pa-1 ml-3 flex-shrink-0"
                      style="background: rgba(255, 255, 255, 0.1)"
                    >
                      <v-img :src="file.url" contain></v-img>
                    </v-avatar>

                    <div
                      class="flex-grow-1 overflow-hidden pl-2"
                      style="min-width: 0"
                    >
                      <div
                        class="font-weight-bold text-subtitle-1 mb-1 text-truncate"
                        :style="{ color: activePalette.textColor }"
                        :title="file.title"
                      >
                        {{ file.title || "فایل ضمیمه" }}
                      </div>
                      <div>
                        <div
                          class="text-caption text-wrap-custom transition-all"
                          :class="{ 'line-clamp-2': !isExpanded(file.link) }"
                          :style="{
                            color: activePalette.textColor,
                            opacity: 0.7,
                          }"
                          :title="file.description"
                        >
                          {{ file.description || "بدون توضیحات" }}
                        </div>
                        <div
                          v-if="hasLongText(file.description)"
                          @click="toggleDesc(file.link)"
                          class="read-more-btn mt-1"
                          :style="{ color: activePalette.textColor }"
                        >
                          {{
                            isExpanded(file.link)
                              ? "بستن متن"
                              : "مشاهده بیشتر..."
                          }}
                        </div>
                      </div>
                    </div>

                    <div
                      class="d-flex flex-column align-center justify-center flex-shrink-0 mr-2 gap-1"
                    >
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            :class="
                              activeFile && activeFile.link === file.link
                                ? 'btn-get-code rounded-pill'
                                : 'btn-download rounded-pill'
                            "
                            @click="openInline(file)"
                            v-bind="attrs"
                            v-on="on"
                            small
                          >
                            <v-icon small>{{
                              activeFile && activeFile.link === file.link
                                ? "mdi-eye-off"
                                : "mdi-eye"
                            }}</v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          activeFile && activeFile.link === file.link
                            ? "بستن"
                            : "مشاهده در صفحه"
                        }}</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            class="btn-get-code rounded-pill"
                            :href="file.link"
                            download
                            v-bind="attrs"
                            v-on="on"
                            small
                          >
                            <v-icon small>mdi-download</v-icon>
                          </v-btn>
                        </template>
                        <span>دانلود مستقیم</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-list-item>

                <v-divider
                  v-if="fileIndex < category.images.length - 1"
                  :key="'div-' + fileIndex"
                  class="my-1"
                ></v-divider>
              </template>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- ۲. حالت لیست -->
      <div v-else-if="activeLayout === 'list'">
        <div v-for="(category, index) in pdfData" :key="index" class="mb-4">
          <h4
            class="mb-3 px-2 font-weight-bold"
            v-if="category.name"
            :style="{ color: activePalette.textColor }"
          >
            {{ category.name }}
          </h4>
          <v-card
            v-for="file in category.images"
            :key="file.link"
            elevation="1"
            class="d-flex flex-row align-center pa-3 mb-3 rounded-xl hover-card border-all"
            :style="{
              background: activePalette.bodyColor,
              borderColor:
                activeFile && activeFile.link === file.link
                  ? activePalette.borderColor
                  : 'transparent',
            }"
          >
            <v-avatar
              rounded
              size="80"
              class="ml-3 pa-1 flex-shrink-0"
              style="background: rgba(255, 255, 255, 0.05)"
            >
              <v-img :src="file.url" contain></v-img>
            </v-avatar>

            <!-- متن -->
            <div class="flex-grow-1 overflow-hidden" style="min-width: 0">
              <div
                class="text-subtitle-1 font-weight-bold mb-1 text-truncate"
                :style="{ color: activePalette.textColor }"
                :title="file.title"
              >
                {{ file.title || "فایل ضمیمه" }}
              </div>
              <div>
                <div
                  class="text-caption text-wrap-custom"
                  :class="{ 'line-clamp-2': !isExpanded(file.link) }"
                  :style="{ color: activePalette.textColor, opacity: 0.8 }"
                  :title="file.description"
                >
                  {{ file.description || "آماده برای مشاهده و دانلود" }}
                </div>
                <div
                  v-if="hasLongText(file.description)"
                  @click="toggleDesc(file.link)"
                  class="read-more-btn mt-1"
                  :style="{ color: activePalette.textColor }"
                >
                  {{ isExpanded(file.link) ? "بستن متن" : "مشاهده بیشتر..." }}
                </div>
              </div>
            </div>

            <div class="d-flex flex-column flex-shrink-0 mr-2 gap-1">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    :class="
                      activeFile && activeFile.link === file.link
                        ? 'btn-get-code rounded-lg'
                        : 'btn-download rounded-lg'
                    "
                    @click="openInline(file)"
                  >
                    <v-icon small class="ml-1">{{
                      activeFile && activeFile.link === file.link
                        ? "mdi-eye-off"
                        : "mdi-eye"
                    }}</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{
                    activeFile && activeFile.link === file.link
                      ? "بستن"
                      : "مشاهده"
                  }}</span
                >
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    icon
                    class="btn-get-code"
                    depressed
                    :href="file.link"
                    v-bind="attrs"
                    v-on="on"
                    download
                  >
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>دانلود</span>
              </v-tooltip>
            </div>
          </v-card>
        </div>
      </div>

      <!-- ۳. حالت اسلایدر -->
      <div v-else-if="activeLayout === 'slider'">
        <div v-for="(category, index) in pdfData" :key="index" class="mb-5">
          <h4
            class="mb-2 px-1 font-weight-bold"
            v-if="category.name"
            :style="{ color: activePalette.textColor }"
          >
            {{ category.name }}
          </h4>
          <v-slide-group show-arrows>
            <v-slide-item v-for="file in category.images" :key="file.link">
              <v-card
                class="ma-2 rounded-xl d-flex flex-column pa-3 hover-card border-all"
                width="190"
                min-height="240"
                elevation="2"
                :style="{
                  background: activePalette.bodyColor,
                  borderColor:
                    activeFile && activeFile.link === file.link
                      ? activePalette.borderColor
                      : 'transparent',
                }"
              >
                <div
                  class="slider-img-container rounded-lg d-flex justify-center align-center mb-3 pa-1"
                  style="background: rgba(255, 255, 255, 0.05); height: 120px"
                >
                  <v-img
                    :src="file.url"
                    max-height="100"
                    max-width="100"
                    contain
                  ></v-img>
                </div>

                <div
                  class="text-center text-subtitle-2 font-weight-bold text-wrap-custom w-100 mb-1"
                  :style="{ color: activePalette.textColor, lineHeight: '1.4' }"
                  :title="file.title"
                >
                  {{ file.title || "فایل" }}
                </div>
                <div class="mb-2 d-flex flex-column flex-grow-1">
                  <div
                    class="text-center text-caption text-wrap-custom flex-grow-1"
                    :class="{ 'line-clamp-2': !isExpanded(file.link) }"
                    :style="{
                      color: activePalette.textColor,
                      opacity: 0.7,
                      fontSize: '0.7rem',
                    }"
                    :title="file.description"
                  >
                    {{ file.description || "" }}
                  </div>
                  <div
                    v-if="hasLongText(file.description)"
                    @click="toggleDesc(file.link)"
                    class="read-more-btn mt-1 text-center w-100"
                    :style="{ color: activePalette.textColor }"
                  >
                    {{ isExpanded(file.link) ? "بستن" : "بیشتر..." }}
                  </div>
                </div>

                <v-spacer></v-spacer>

                <div class="d-flex justify-space-between w-100 mt-1 gap-1">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        icon
                        v-on="on"
                        v-bind="attrs"
                        :class="
                          activeFile && activeFile.link === file.link
                            ? 'btn-get-code rounded-pill'
                            : 'btn-download rounded-pill'
                        "
                        @click="openInline(file)"
                        title="مشاهده"
                      >
                        <v-icon small>{{
                          activeFile && activeFile.link === file.link
                            ? "mdi-eye-off"
                            : "mdi-eye"
                        }}</v-icon>
                      </v-btn>
                    </template>
                    <span>مشاهده</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        icon
                        v-on="on"
                        v-bind="attrs"
                        outlined
                        :href="file.link"
                        download
                        class="btn-get-code rounded-pill"
                      >
                        <v-icon small class="mr-1">mdi-download</v-icon>
                      </v-btn>
                    </template>
                    <span>دانلود</span>
                  </v-tooltip>
                </div>
              </v-card>
            </v-slide-item>
          </v-slide-group>
        </div>
      </div>
    </div>

    <!-- ---------------------------------------------------- -->
    <!-- نمایشگر Iframe (مشاهده درون برنامه) -->
    <!-- ---------------------------------------------------- -->
    <v-expand-transition>
      <div v-if="activeFile" class="mt-4 viewer-container">
        <v-card
          outlined
          class="rounded-lg overflow-hidden"
          :style="{ borderColor: activePalette.borderColor }"
        >
          <div
            class="d-flex justify-space-between align-center pa-3 border-bottom"
            :style="{
              background: activePalette.headColor,
              borderColor: activePalette.borderColor,
            }"
          >
            <div class="d-flex align-center overflow-hidden pr-2">
              <v-icon :color="activePalette.textColor" class="ml-2"
                >mdi-file-eye-outline</v-icon
              >
              <span
                class="font-weight-bold text-truncate"
                :style="{ color: activePalette.textColor }"
                :title="activeFile.title"
              >
                {{ activeFile.title || "در حال نمایش فایل" }}
              </span>
            </div>
            <div
              class="d-flex flex-row justify-center align-center align-items-center"
            >
              <v-btn
                icon
                :color="activePalette.textColor"
                :href="activeFile.link"
                target="_blank"
                title="باز کردن در تب جدید (پنجره کامل)"
              >
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
              <v-btn
                icon
                class="btn-delete rounded-pill"
                @click="activeFile = null"
                title="بستن نمایشگر"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <div class="iframe-wrapper bg-white">
            <iframe
              :src="activeFile.link"
              width="100%"
              height="600px"
              frameborder="0"
              class="file-iframe"
            ></iframe>
          </div>
        </v-card>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
export default {
  name: "PdfPreviewDisplay",
  props: {
    pdfData: {
      type: Array,
      default: () => [],
    },
    layoutMode: {
      type: String,
      default: null,
    },
    palette: {
      type: Object,
      default: null,
    },
    pdfSelector: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeFile: null,
      expandedDesc: [],
    };
  },
  computed: {
    activePalette() {
      const defaultPalette = {
        headColor: "#f5f5f5",
        bodyColor: "#ffffff",
        textColor: "#000000",
        borderColor: "#e0e0e0",
      };

      if (this.palette && Object.keys(this.palette).length > 0) {
        return { ...defaultPalette, ...this.palette };
      }

      if (this.pdfSelector && this.pdfSelector.sectionColor) {
        return {
          ...defaultPalette,
          bodyColor:
            this.pdfSelector.sectionColor.backGround ||
            defaultPalette.bodyColor,
          textColor:
            this.pdfSelector.sectionColor.text || defaultPalette.textColor,
        };
      }

      return defaultPalette;
    },

    activeLayout() {
      if (this.layoutMode) {
        return this.layoutMode;
      }
      if (this.pdfSelector && this.pdfSelector.layout) {
        return this.pdfSelector.layout;
      }
      return "list";
    },

    allFiles() {
      let files = [];
      if (this.pdfData && this.pdfData.length > 0) {
        this.pdfData.forEach((category) => {
          if (category.images && category.images.length > 0) {
            files = files.concat(category.images);
          }
        });
      }
      return files;
    },
    totalFilesCount() {
      return this.allFiles.length;
    },
    singleFile() {
      return this.totalFilesCount === 1 ? this.allFiles[0] : null;
    },
  },
  methods: {
    getFileIcon(path) {
      if (!path) return "media/file.svg";
      const ext = path.split(".").pop().toLowerCase();
      switch (ext) {
        case "doc":
        case "docx":
          return `media/doc.webp`;
        case "pdf":
          return `media/pdf.webp`;
        case "txt":
          return `media/txt.webp`;
        default:
          return "media/file.webp";
      }
    },
    openInline(file) {
      if (this.activeFile && this.activeFile.link === file.link) {
        this.activeFile = null;
      } else {
        this.activeFile = file;
        this.$nextTick(() => {
          setTimeout(() => {
            const viewer = document.querySelector(".viewer-container");
            if (viewer) {
              viewer.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }, 300);
        });
      }
    },
    hasLongText(text) {
      return text && text.length > 50;
    },
    toggleDesc(link) {
      if (this.expandedDesc.includes(link)) {
        this.expandedDesc = this.expandedDesc.filter((item) => item !== link);
      } else {
        this.expandedDesc.push(link);
      }
    },
    isExpanded(link) {
      return this.expandedDesc.includes(link);
    },
  },
  watch: {
    pdfData: {
      deep: true,
      handler() {
        this.activeFile = null;
        this.expandedDesc = [];
        this.$forceUpdate();
      },
    },
  },
};
</script>

<style scoped>
.file-preview-container {
  width: 100%;
  padding: 8px;
}

.text-wrap-custom {
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  transition: all 0.3s ease;
}

.read-more-btn {
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-block;
  opacity: 0.9;
  text-decoration: underline;
  text-decoration-style: dotted;
  transition: opacity 0.2s;
  color: rgb(40, 40, 238);
}
.read-more-btn:hover {
  opacity: 1;
}

.w-100 {
  width: 100%;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}

.border-all {
  border-width: 1px;
  border-style: solid;
}
.border-bottom {
  border-bottom-width: 1px;
  border-bottom-style: solid;
}

.hover-card {
  transition: all 0.3s ease;
}
.hover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}
.file-list-item {
  transition: background-color 0.2s;
}

.thumbnail-wrapper {
  min-width: 150px;
  min-height: 150px;
}
.slider-img-container {
  width: 100%;
  overflow: hidden;
}

.iframe-wrapper {
  position: relative;
  width: 100%;
  background: #f5f5f5
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc" width="48px" height="48px"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-4h2v2h-2zm1-12C9.79 4 8 5.79 8 8h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"/></svg>')
    no-repeat center center;
}
.file-iframe {
  position: relative;
  z-index: 1;
  background: white;
}
::v-deep .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
